import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { getAllRouteParams } from 'src/app/lib/get-all-route-params';
import { getFullPath } from 'src/app/lib/get-full-path';
import { GalleryPasswordService } from 'src/app/services/gallery-password.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { GuestAuthService } from 'src/app/services/guest-auth.service';
import { GuestService } from 'src/app/services/guest.service';
import { PermissionService } from 'src/app/services/permission.service';
import { RedirectService } from 'src/app/services/redirect.service';
import { StateService } from 'src/app/services/state.service';
import { UserService } from 'src/app/services/user.service';
import { VisitorService } from 'src/app/services/visitor.service';
import { getBaseWithoutKey } from '../libs/base-without-key';

@Injectable({
  providedIn: 'root',
})
export class ViewGalleryGuard {
  constructor(
    private galleryService: GalleryService,
    private state: StateService,
    private userService: UserService,
    private redirectService: RedirectService,
    private guestAuthService: GuestAuthService,
    private guestService: GuestService,
    private galleryPasswordService: GalleryPasswordService,
    private permissionService: PermissionService,
    private visitorService: VisitorService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    if (isPlatformBrowser(this.platformId)) {
      await this.userService.getUser();
      await this.guestService.getGuest();
    }
    const resp = await this.handleGalleryPath(next);

    if (resp === true) {
      const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next, { isPublic: true });

      if (next.queryParams.preview) {
        this.galleryService.fakeGalleryView(gallery.id);
        return true;
      }

      // If we are allowed to view this gallery, we will report a gallery view to the backend
      if (isPlatformBrowser(this.platformId) && resp === true) {
        if (this.state.guest || this.state.user) {
          this.visitorService.createVisitor(gallery.id);
        }
        try {
          this.galleryService.galleryView(gallery.id);
        } catch (err) {
          setTimeout(() => {
            // throwing error only after timeout because gallery view is non critical
            if (!(err instanceof HttpErrorResponse)) throw err;
            if (err.status !== 404 && err.status !== 403) throw err;
            // Ignoring 404 errors here.. Seems that this just happens sometimes and I have no clue why
          });
        }
      }
    }

    return resp;
  }

  public async handleGalleryPath(next: ActivatedRouteSnapshot, ignoreIdentity = false, idOrPath?: string) {
    if (!idOrPath) {
      const params = getAllRouteParams(next);
      idOrPath = params.galleryPath || params.galleryId;
    }
    const fullPath = getFullPath(next);
    const { baseWithoutKey, rest, base } = getBaseWithoutKey(fullPath, idOrPath);
    const query = next.queryParams;
    try {
      const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next, {
        isPublic: true,
        ignore401Errors: true,
        ignore503Errors: true,
      });

      if (gallery.requires_user_identification && !ignoreIdentity) {
        await this.guestAuthService.init();
        const userLoggedIn = !!(await this.userService.getUser());
        const guestLoggedIn = !!this.state.guestAuthToken;
        if (!guestLoggedIn && !userLoggedIn) {
          // Guest auth is required to view this gallery
          // And there is no guest token so we redirect to the identify page

          if (this.permissionService.canDisplayElement('gallery.forceIdentification', gallery)) {
            return this.redirectService.redirectTo([...base, 'identify', ...rest], query);
          }
        }
      }

      return true;
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error;
      if (error.status === 404 || error.status === 403) {
        this.state.nextPageIsNotFound = true;
        return this.redirectService.redirectTo([...baseWithoutKey, 'not-found', ...rest], query);
      }

      // Temporary workaround: https://gitlab.com/nusign/picflow/picflow-backend/-/issues/260
      const backendError500InsteadOf401 = error.status === 500 && error.error?.message?.includes('and "password" = $');

      if (error.status === 401 || error.status === 403 || backendError500InsteadOf401) {
        this.galleryPasswordService.removePassword(idOrPath);
        return this.redirectService.redirectTo([...baseWithoutKey, 'access', ...rest], query);
      }

      if (error.status === 503) {
        return this.redirectService.redirectTo([...baseWithoutKey, 'maintenance', ...rest], query);
      }

      return this.redirectService.redirectTo([...baseWithoutKey, 'error', ...rest], query);
    }
  }
}
